import React, { useState } from "react";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import Modal from 'react-bootstrap/Modal';
import { trackEvent } from "../lib/analytics";

import Footer from "./Footer";
import Nav from "./Nav";
import { SEO_SHARE_IMAGES } from "../lib/seoImages";
import "../styles/homepage-redesign.css";
import "../styles/contact.css";

function ContactUs() {
  const pageUrl = "https://speakyourmenu.com/contact";
  const shareImage = SEO_SHARE_IMAGES.contact;

  const [errors, setErrors] = useState({});
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [show, setShow] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);


  const sendEmail = () => {
    const templateParams = {
      name: userInfo.name,
      email: userInfo.email,
      subject: userInfo.subject,
      message: userInfo.message,
    };

    return emailjs.send(
      "service_bk8ljie",
      "template_gu740jm",
      templateParams,
      "PByxhVdkuDB9scoXI"
    );
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setUserInfo((prevState) => ({ ...prevState, [name]: value }));
    if (errors[name]) {
      setErrors((prevState) => ({ ...prevState, [name]: undefined }));
    }
  }

  function isValidEmail(email) {
    // Define a regular expression pattern for email validation.
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return;

    let validationErrors = {};

    if (userInfo.email.length === 0 || !isValidEmail(userInfo.email)) {
      validationErrors.email = 'Email is invalid.'
    }

    if (userInfo.name.length === 0){
      validationErrors.name = 'Please enter a name.'
    }

    if (userInfo.subject.length === 0) {
      validationErrors.subject = 'Please enter a subject.'
    }

    if (userInfo.message.length === 0) {
      validationErrors.message = 'Please enter a message.'
    }


    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await sendEmail();

      setSuccessEmail(String(userInfo.email || "").trim());
      handleShow();
      setUserInfo({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      trackEvent("generate_lead", {
        lead_type: "contact",
        form_location: "contact_page",
        page_path: "/contact",
      });
    } catch (error) {
      setErrors({
        submit: "We couldn't send your message right now. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }


  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Contact Us | Restaurant Training & Menu Education Support</title>
        <meta name="description" content="Contact Speak Your Menu for restaurant training, menu education, and front of house staff support. Get in touch with our team for help with menu training and onboarding." />
        {/* Open Graph */}
        <meta property="og:title" content="Contact Us | Restaurant Training & Menu Education Support" />
        <meta property="og:description" content="Contact Speak Your Menu for restaurant training, menu education, and front of house staff support. Get in touch with our team for help with menu training and onboarding." />
        <meta property="og:type" content="website" />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="Speak Your Menu" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Restaurant Training & Menu Education Support" />
        <meta name="twitter:description" content="Contact Speak Your Menu for restaurant training, menu education, and front of house staff support. Get in touch with our team for help with menu training and onboarding." />
          <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:site" content="@speakyourmenu" />
        {/* Canonical URL */}
          <link rel="canonical" href={pageUrl} />
        {/* BreadcrumbList JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://speakyourmenu.com/"},
            {"@type": "ListItem", "position": 2, "name": "Contact", "item": "https://speakyourmenu.com/contact"}
          ]
        })}</script>
        {/* Organization JSON-LD is below. To update keywords or URLs, edit above meta tags and JSON-LD. */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Speak Your Menu",
            "url": "https://speakyourmenu.com",
            "logo": "https://speakyourmenu.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-212-555-0199",
              "contactType": "Customer Support",
              "areaServed": "US",
              "availableLanguage": ["English", "Spanish"]
            },
            "sameAs": [
              "https://www.instagram.com/speakyourmenu",
              "https://www.facebook.com/speakyourmenu",
              "https://www.linkedin.com/company/speakyourmenu"
            ]
          }
        `}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="contact-page homepage-redesign">
        <section className="homepage-section homepage-hero-section contact-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell contact-hero-shell">
            <div className="contact-hero-copy">
              <h1 className="homepage-hero-title contact-hero-title">Contact Us</h1>
              <p className="homepage-hero-subhead contact-hero-subhead">
                Send us a note, we typically reply within 1 business day.
              </p>
            </div>
          </div>
        </section>

        <section className="homepage-section contact-main-section">
          <div className="homepage-shell">
            <div className="contact-support-shell">
              <div className="contact-support-intro">
                <p className="contact-guidance-intro">
                  Questions about pricing, setup, or multi-location? Send us a message.
                </p>
              </div>

              <div className="contact-support-grid" aria-label="Contact guidance">
                <article className="homepage-surface-card contact-support-card">
                  <p className="contact-guidance-note-title">We can help with</p>
                  <ul className="contact-guidance-list">
                    <li>Plans and multi-location pricing</li>
                    <li>Help getting started</li>
                    <li>Product questions</li>
                  </ul>
                </article>

                <article className="homepage-surface-card contact-support-card contact-support-card-accent">
                  <p className="contact-guidance-note-title">Fastest help</p>
                  <p className="contact-guidance-note-body">
                    For fastest help, include your restaurant name and number of locations.
                  </p>
                  <p className="contact-guidance-email">
                    Prefer email?{" "}
                    <a href="mailto:support@speakyourmenu.com">support@speakyourmenu.com</a>
                  </p>
                </article>
              </div>
            </div>

            <div className="contact-form-shell">
              <section className="homepage-surface-card contact-form-card" aria-labelledby="contact-form-heading">
                <div className="contact-form-header">
                  <p className="contact-form-kicker">Send a message</p>
                  <h2 id="contact-form-heading" className="contact-form-title">Tell us what you need</h2>
                  <p className="contact-form-copy">
                    Share a few details and our team will follow up with the right next step.
                  </p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-group contact-form-group">
                    <label className="contact-form-label" htmlFor="contact-name">Name</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      className="form-control form-control-lg contact-form-input"
                      placeholder="John Doe"
                      value={userInfo.name}
                      onChange={handleChange}
                      autoFocus
                      autoComplete="name"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "contact-name-error" : undefined}
                    />
                    {errors.name && <div id="contact-name-error" className="text-danger small mt-2" role="alert">{errors.name}</div>}
                  </div>

                  <div className="form-group contact-form-group">
                    <label className="contact-form-label" htmlFor="contact-email">Email</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      className="form-control form-control-lg contact-form-input"
                      placeholder="name@example.com"
                      value={userInfo.email}
                      onChange={handleChange}
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                    />
                    {errors.email && <div id="contact-email-error" className="text-danger small mt-2" role="alert">{errors.email}</div>}
                  </div>

                  <div className="form-group contact-form-group">
                    <label className="contact-form-label" htmlFor="contact-subject">Subject</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="form-control form-control-lg contact-form-input"
                      placeholder="Contact Subject"
                      value={userInfo.subject}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={errors.subject ? "contact-subject-error" : undefined}
                    />
                    {errors.subject && <div id="contact-subject-error" className="text-danger small mt-2" role="alert">{errors.subject}</div>}
                  </div>

                  <div className="form-group contact-form-group">
                    <label className="contact-form-label" htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      className="form-control form-control-lg contact-form-input contact-form-textarea"
                      rows="4"
                      maxLength="500"
                      value={userInfo.message}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? "contact-message-error" : undefined}
                    ></textarea>
                    <div className="contact-form-count" aria-live="polite">{userInfo.message.length}/500</div>
                    {errors.message && <div id="contact-message-error" className="text-danger small mt-2" role="alert">{errors.message}</div>}
                  </div>

                  <div className="contact-form-actions">
                    <button
                      type="submit"
                      className="homepage-primary-cta contact-submit-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Contact Us"}
                    </button>
                  </div>
                  {errors.submit ? <div className="text-danger small mt-2" role="alert">{errors.submit}</div> : null}
                </form>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Modal show={show} onHide={handleClose} animation={false} centered contentClassName="contact-success-modal">
        <Modal.Header closeButton>
          <Modal.Title>Message sent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="contact-success-message">
            Message sent. We'll reply to {successEmail} within 1 business day.
          </p>
          <p className="contact-success-note">If you don't see a reply, check spam.</p>
        </Modal.Body>
      </Modal>
      <Footer />
    </div>
  );
}

export default ContactUs;
